import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/auth'
import { authApi } from '@eshop/shared'
import { useLanguage } from '@eshop/shared'

export function Register() {
  const navigate = useNavigate()
  const { t } = useLanguage()
  const { setAuth, isAuthenticated } = useAuthStore()
  const [email, setEmail] = useState('')
  const [password1, setPassword1] = useState('')
  const [password2, setPassword2] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/backoffice')
    }
  }, [isAuthenticated, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password1 !== password2) {
      setError(t('passwordsDoNotMatch'))
      return
    }

    setLoading(true)

    try {
      const response = await authApi.register(email, password1, password2)
      setAuth(response.user, response.tokens)
      navigate('/backoffice')
    } catch (err: any) {
      console.error('Registration failed:', err)
      const errors = err.response?.data
      if (errors) {
        // Handle different error formats
        if (errors.email) {
          setError(Array.isArray(errors.email) ? errors.email[0] : errors.email)
        } else if (errors.password1) {
          setError(Array.isArray(errors.password1) ? errors.password1[0] : errors.password1)
        } else if (errors.non_field_errors) {
          setError(Array.isArray(errors.non_field_errors) ? errors.non_field_errors[0] : errors.non_field_errors)
        } else {
          setError(t('registrationFailed'))
        }
      } else {
        setError(t('registrationFailed'))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <h1 className="text-3xl font-bold text-center mb-2 text-gray-900">
          {t('createAccount')}
        </h1>
        <p className="text-center text-gray-600 mb-8">
          {t('loginSubtitle')}
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('email')}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('emailPlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('password')}
            </label>
            <input
              type="password"
              value={password1}
              onChange={(e) => setPassword1(e.target.value)}
              required
              minLength={8}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('passwordPlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('confirmPassword')}
            </label>
            <input
              type="password"
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              required
              minLength={8}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('passwordPlaceholder')}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:bg-blue-400"
          >
            {loading ? t('registering') : t('register')}
          </button>
        </form>

        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/login')}
            className="text-blue-600 hover:text-blue-700 text-sm"
          >
            {t('alreadyHaveAccount')} <span className="font-semibold">{t('signIn')}</span>
          </button>
        </div>
      </div>
    </div>
  )
}
